import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import {
  xSurvey,
  SurveyAnswerNode,
  SurveyTemplateResponse,
  SurveyRulesResponse,
  SurveyAnswerPost,
  SurveyAnswerRequest,
  SurveyAnswerSectionScore,
  surveyModuleType,
  surveyActors,
  SurveyMultiFarmFilter,
} from '../_models/xsurvey';
import { FarmService } from './farm.service';
import { Farm } from '../_models';
import { xSurveyUtilitiesService } from './survey/xsurveyUtilities.service';
import { UserService } from './user.service';
import { CommonContractDataService } from './contract-management/common.service';
import { CommonSupplierItem } from '../_models/contractManagement/common';

@Injectable()
export class XsurveyService {
  subscriptions: Subscription = new Subscription();

  private surveyService = environment.surveyServiceUrl;
  private headers = new HttpHeaders();
  private multiFarmFilter: SurveyMultiFarmFilter = null;
  private currentModule: string = null;

  private chainKey: string | null = null;
  private userRole: string | null = null;

  recallGetSurveys: EventEmitter<{ hasDataChanged?: boolean; hasStatusChanged?: boolean }> =
    new EventEmitter();

  totalSurveys$: BehaviorSubject<number> = new BehaviorSubject<number>(null);
  public suppliersInChain$: BehaviorSubject<CommonSupplierItem[]> = new BehaviorSubject<
    CommonSupplierItem[]
  >(null);
  public cropYear: number = null;

  constructor(
    private http: HttpClient,
    private farmService: FarmService,
    private xSurveyUtilitiesService: xSurveyUtilitiesService,
    private userService: UserService,
    private commonContractDataService: CommonContractDataService
  ) {}

  async getSurveysNumberByPost(
    year: number,
    type: string = surveyActors.ALL,
    moduleType: string
  ): Promise<any> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .post<any>(
        `${this.surveyService}api/v2/private/survey/${type}/count/?cropYear=${year}&moduleType=${moduleType}`,
        this.multiFarmFilter,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  async getAllSurveysByPost(
    year: number,
    type: string = surveyActors.ALL,
    moduleType: string,
    additionalQueryParams?: any
  ): Promise<{ data: xSurvey[]; totalCount: number }> {
    this.handleParamsAndHeaders();

    let params = new HttpParams().set('cropYear', year.toString());

    if (moduleType) {
      params = params.set('moduleType', moduleType);
    }

    // Not currently used
    // To be used for filtering logic on BE
    if (additionalQueryParams) {
      if ('size' in additionalQueryParams) params.set('size', additionalQueryParams.size);
      if ('page' in additionalQueryParams) params.set('page', additionalQueryParams.page);
      if ('companyIdAssignee' in additionalQueryParams)
        params.set('companyIdAssignee', additionalQueryParams.companyIdAssignee);
      if ('companyIdAnswerLastModify' in additionalQueryParams)
        params.set('companyIdAnswerLastModify', additionalQueryParams.companyIdAnswerLastModify);
      if ('status' in additionalQueryParams) params.set('status', additionalQueryParams.status);
      if ('result' in additionalQueryParams) params.set('result', additionalQueryParams.result);
      if ('dateCreated' in additionalQueryParams)
        params.set('dateCreated', additionalQueryParams.dateCreated);
      if ('lastModify' in additionalQueryParams)
        params.set('lastModify', additionalQueryParams.lastModify);
      if ('roleFarmAssignee' in additionalQueryParams)
        params.set('roleFarmAssignee', additionalQueryParams.roleFarmAssignee);
      if ('roleFarmLastAnswer' in additionalQueryParams)
        params.set('roleFarmLastAnswer', additionalQueryParams.roleFarmLastAnswer);
      if ('chainKey' in additionalQueryParams)
        params.set('chainKey', additionalQueryParams.chainKey);
      if ('orderBy' in additionalQueryParams) params.set('orderBy', additionalQueryParams.orderBy);
      if ('descending' in additionalQueryParams)
        params.set('descending', additionalQueryParams.descending);
    }

    const response = await this.http
      .post<any>(
        `${this.surveyService}api/v2/private/survey/${type}/?${params.toString()}`,
        this.multiFarmFilter,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  async getSingleSurveyByPost(surveyId: string): Promise<xSurvey> {
    this.handleParamsAndHeaders();

    const params = new URLSearchParams();
    if (this.currentModule == surveyModuleType.AUDIT) {
      params.append('cropYear', this.cropYear.toString());
    }

    const response = await this.http
      .post<any>(
        `${this.surveyService}api/v2/private/survey/${surveyActors.ALL}/${surveyId}?${params.toString()}`,
        this.multiFarmFilter,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  async getSurveyAsOwnerByTemplate(
    templateId: string
  ): Promise<{ data: xSurvey[]; totalCount: number }> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(
        `${this.surveyService}api/v2/private/survey/${surveyActors.OWNER}/byTemplate/${templateId}`,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  async getSurveyAsAssigneeByTemplate(
    templateId: string
  ): Promise<{ data: xSurvey[]; totalCount: number }> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(
        `${this.surveyService}api/v2/private/survey/${surveyActors.ASSIGNEE}/byTemplate/${templateId}`,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  async getAllSurveyTemplatesByYear(year: number): Promise<SurveyTemplateResponse[]> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(`${this.surveyService}v1/private/template?surveyCropYear=${year}`, {
        headers: this.headers,
      })
      .toPromise();
    return response;
  }

  async getSurveyTemplateByPost(templateId: string): Promise<SurveyTemplateResponse> {
    this.handleParamsAndHeaders();

    const params = new URLSearchParams();
    if (this.currentModule == surveyModuleType.AUDIT) {
      params.append('cropYear', this.cropYear.toString());
    }

    const response = await this.http
      .post<any>(
        `${this.surveyService}v1/private/template/${templateId}?${params.toString()}`,
        this.multiFarmFilter,
        { headers: this.headers }
      )
      .toPromise();
    response.template = JSON.parse(response.template);
    return response;
  }

  async getSurveyAnswers(surveyId: string): Promise<SurveyAnswerRequest> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(`${this.surveyService}api/v2/private/answer/bySurvey/${surveyId}`, {
        headers: this.headers,
      })
      .toPromise();
    return response || {};
  }

  async getSurveyAnswersByPost(surveyId: string): Promise<SurveyAnswerRequest> {
    this.handleParamsAndHeaders();

    const params = new URLSearchParams();
    if (this.currentModule == surveyModuleType.AUDIT) {
      params.append('cropYear', this.cropYear.toString());
    }

    const response = await this.http
      .post<any>(
        `${this.surveyService}api/v2/private/answer/bySurvey/${surveyId}?${params.toString()}`,
        this.multiFarmFilter,
        { headers: this.headers }
      )
      .toPromise();
    return response || {};
  }

  async getSurveyRules(templateId: string): Promise<SurveyRulesResponse> {
    this.handleParamsAndHeaders();

    try {
      const response = await this.http
        .get<any>(`${this.surveyService}v1/private/logic/byTemplate/${templateId}`, {
          headers: this.headers,
        })
        .toPromise();
      if (response) {
        response.logic = JSON.parse(response.logic);
        return response;
      } else {
        console.error('Empty response body');
        return null;
      }
    } catch (error) {
      console.error('Error fetching survey rules:', error);
      return null;
    }
  }

  async saveSurveyAnswers(
    surveyId: string,
    answers: SurveyAnswerNode[],
    formModel: string,
    status: string
  ): Promise<SurveyAnswerNode[]> {
    this.handleParamsAndHeaders();

    const params = new URLSearchParams();
    if (this.currentModule == surveyModuleType.AUDIT) {
      params.append('cropYear', this.cropYear.toString());
    }

    const request: SurveyAnswerPost = {
      answers,
      formModel,
      surveyStatus: status,
    };

    const response = await this.http
      .post<any>(
        `${this.surveyService}v1/private/answer/${surveyId}/?${params.toString()}`,
        request,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  async startSurveyCalculation(surveyId: string): Promise<any> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .put<any>(
        `${this.surveyService}v1/private/answer/calculate/start/${surveyId}`,
        {},
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  async getSurveyCalculation(surveyId: string): Promise<SurveyAnswerSectionScore[]> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(`${this.surveyService}v1/private/answer/calculate/${surveyId}`, {
        headers: this.headers,
      })
      .toPromise();
    return response;
  }

  async getSurveySupplyChainAvgScore(templateId: string, supplyChain: string): Promise<number> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(
        `${this.surveyService}v1/private/statistic/avgScoreByChain/?templateId=${templateId}&chainKey=${supplyChain}`,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  // Not used
  async countAllSurveys(
    year: number,
    type: string = surveyActors.ALL,
    moduleType?: string
  ): Promise<any> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(
        `${this.surveyService}api/v2/private/survey/${type}/count/?cropYear=${year}&moduleType=${moduleType}`,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  // Not used
  async getAllSurvey(
    year: number,
    type: string = surveyActors.ALL,
    moduleType?: string
  ): Promise<{ data: xSurvey[]; totalCount: number }> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(
        `${this.surveyService}api/v2/private/survey/${type}/?cropYear=${year}&moduleType=${moduleType}`,
        { headers: this.headers }
      )
      .toPromise();
    return response;
  }

  // Not used
  async getAllSurveyTemplates(): Promise<SurveyTemplateResponse[]> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(`${this.surveyService}v1/private/template`, { headers: this.headers })
      .toPromise();
    return response;
  }

  // Not used
  async getSurveyTemplate(templateId?: string): Promise<SurveyTemplateResponse> {
    this.handleParamsAndHeaders();

    const response = await this.http
      .get<any>(`${this.surveyService}v1/private/template/${templateId}`, { headers: this.headers })
      .toPromise();
    response.template = JSON.parse(response.template);
    return response;
  }

  getSurveysNumber(): Observable<any> {
    this.handleParamsAndHeaders();

    return this.http
      .post<any>(
        `${this.surveyService}api/v2/private/survey/${surveyActors.ALL}/count/?moduleType=${surveyModuleType.SURVEY}`,
        this.multiFarmFilter,
        { headers: this.headers }
      )
      .pipe(
        map((response) => {
          this.totalSurveys$.next(response);
          return response;
        })
      );
  }

  formatUnit(udm: string) {
    switch (udm) {
      case 'unit': {
        return 'N\u00B0';
      }
      case 'mq': {
        return 'm\u00B2';
      }
      default:
        return udm;
    }
  }

  handleParamsAndHeaders() {
    this.currentModule = this.xSurveyUtilitiesService.getCurrentModule();

    this.retrieveHeaders();
    this.toggleHeaders();
    this.getMultiFarmFilters();
  }

  retrieveHeaders() {
    this.chainKey =
      this.farmService.getLocalCurrentFarm().groups?.length &&
      this.farmService.getLocalCurrentFarm().groups[0].group
        ? this.farmService.getLocalCurrentFarm().groups[0].group
        : null;

    this.userRole =
      this.farmService.getLocalCurrentFarm().groups?.length &&
      this.farmService.getLocalCurrentFarm().groups[0].rolesInGroup?.length &&
      this.farmService.getLocalCurrentFarm().groups[0].rolesInGroup[0]?.authority
        ? this.farmService.getLocalCurrentFarm().groups[0].rolesInGroup[0].authority
        : null;
  }

  toggleHeaders() {
    if (
      !this.currentModule ||
      (this.currentModule == surveyModuleType.SURVEY &&
        this.headers.has('chainKey') &&
        this.headers.has('role'))
    ) {
      this.headers = this.headers.delete('chainKey');
      this.headers = this.headers.delete('role');
    }

    if (
      this.currentModule == surveyModuleType.AUDIT &&
      !this.headers.has('chainKey') &&
      !this.headers.has('role')
    ) {
      this.headers = this.headers.set('chainKey', this.chainKey);
      this.headers = this.headers.set('role', this.userRole);
    }
  }

  getMultiFarmFilters() {
    const { currentUser } = this.userService;

    const currentFarm = JSON.parse(localStorage.getItem('companySelected'));

    let allFarmsInMultiFarm: Farm[] = null;

    if (currentFarm && currentUser.farm && currentUser && currentFarm.id == currentUser.farm.id) {
      allFarmsInMultiFarm = this.farmService.list.getValue();
    } else {
      allFarmsInMultiFarm = this.farmService.list
        .getValue()
        .filter((farm) => farm.id == currentFarm.id);
    }

    // For the surveys in Audit we want to retrieve the surveys only for the children companies (suppliers) that are in the MultifarmChain
    const suppliersInChain =
      this.currentModule == surveyModuleType.AUDIT && this.suppliersInChain$.getValue()
        ? this.suppliersInChain$.getValue()
        : [];

    const companyIds =
      allFarmsInMultiFarm && allFarmsInMultiFarm.length
        ? this.currentModule == surveyModuleType.AUDIT
          ? suppliersInChain.map((supplier) => supplier.companyId)
          : allFarmsInMultiFarm.map((farm) => farm.id)
        : [];

    this.multiFarmFilter = {
      multiFarmFilter: {
        companyIds,
      },
    };
  }

  async getFarmInChainByYear(cropYear: number) {
    const suppliers = await this.commonContractDataService
      .getSuppliersWithCropYear(cropYear)
      .toPromise();
    this.suppliersInChain$.next(suppliers);
  }

  setSuppliersInChainByYear(suppliers) {
    this.suppliersInChain$.next(suppliers);
  }

  setCropYear(year: number) {
    if (typeof year === 'number') {
      this.cropYear = year;
    } else {
      console.error('Invalid input: year must be a number');
    }
  }

  // TODO Remove this method when it will be imported into the generalUtilsService
  // Gets the color value for the determined variable
  getColorFromCSSVariable(variableName: string): string {
    // Get the value of the CSS variable from the :root element
    const colorValue = getComputedStyle(document.documentElement).getPropertyValue(variableName);

    // Return the color value, trimming any whitespace
    return colorValue.trim();
  }
}
